/**
 * Player actions.
 *
 * Events are things that happen *to* the player. Actions are things the player
 * does on purpose. Each board node grants exactly one action, which is what
 * makes opportunity cost concrete: investing this year means not resting this
 * year, and not calling your mother either.
 */

import type { Condition, Effect, PlayerState } from './types';
import { annualExpenses, clamp, investableValue, runwayMonths } from './derived';
import { BUSINESS_ARCHETYPE_LIST, CAREER_FIELD_LIST, EDUCATION_PROGRAM_LIST } from './content';

export type ActionGroup =
  | 'career'
  | 'education'
  | 'finance'
  | 'invest'
  | 'business'
  | 'relationships'
  | 'family'
  | 'health'
  | 'leisure'
  | 'retire';

export interface PlayerAction {
  id: string;
  group: ActionGroup;
  label: string;
  detail: string;
  requires?: Condition[];
  /** Effects applied when the action is taken. */
  effects: Effect[];
  /** Resolved at runtime so amounts can scale with the player's situation. */
  dynamic?: (state: PlayerState) => Effect[];
  tags?: string[];
}

/** A fraction of genuine surplus cash, never the emergency buffer. */
function surplus(state: PlayerState, fraction: number): number {
  const buffer = (annualExpenses(state) / 12) * state.finance.emergencyMonthsTarget;
  const available = state.finance.cash - buffer;
  if (available <= 500) return 0;
  return Math.floor(available * fraction);
}

const STATIC_ACTIONS: PlayerAction[] = [
  /* ---------------- career ---------------- */
  {
    id: 'act.career.pushPerformance',
    group: 'career',
    label: 'Go all in at work this year',
    detail: 'Visible effort, longer hours, faster advancement.',
    requires: [{ k: 'careerStatus', anyOf: ['employed', 'selfEmployed', 'founder'] }],
    effects: [
      { k: 'career', op: 'adjust', field: 'performance', delta: 11 },
      { k: 'career', op: 'adjust', field: 'workload', delta: 10 },
      { k: 'burnout', op: 'load', delta: 9 },
      { k: 'stat', stat: 'energy', delta: -7 },
      { k: 'relationshipAll', dim: 'closeness', delta: -3 },
    ],
    tags: ['ambition'],
  },
  {
    id: 'act.career.protectTime',
    group: 'career',
    label: 'Protect your hours',
    detail: 'Hold the line at work. Slower progress, more life.',
    requires: [{ k: 'careerStatus', anyOf: ['employed', 'selfEmployed', 'founder'] }],
    effects: [
      { k: 'career', op: 'adjust', field: 'workload', delta: -12 },
      { k: 'career', op: 'adjust', field: 'performance', delta: -4 },
      { k: 'burnout', op: 'load', delta: -11 },
      { k: 'stat', stat: 'energy', delta: 7 },
      { k: 'stat', stat: 'stress', delta: -7 },
      { k: 'habit', habit: 'workaholism', delta: -8 },
    ],
    tags: ['balance'],
  },
  {
    id: 'act.career.askForRaise',
    group: 'career',
    label: 'Ask for a raise',
    detail: 'Make the case directly. It usually works better than waiting.',
    requires: [
      { k: 'careerStatus', anyOf: ['employed'] },
      { k: 'num', path: 'performance', op: '>=', value: 55 },
    ],
    effects: [
      { k: 'salary', mode: 'mult', value: 1.07 },
      { k: 'career', op: 'adjust', field: 'reputation', delta: 2 },
      { k: 'stat', stat: 'stress', delta: 3 },
    ],
    tags: ['ambition'],
  },
  {
    id: 'act.career.buildReputation',
    group: 'career',
    label: 'Do visible work outside your team',
    detail: 'Write, speak, help others. Slow, compounding reputation.',
    requires: [{ k: 'num', path: 'yearsExperience', op: '>=', value: 2 }],
    effects: [
      { k: 'career', op: 'adjust', field: 'reputation', delta: 9 },
      { k: 'skill', skill: 'communication', delta: 4 },
      { k: 'stat', stat: 'energy', delta: -4 },
      { k: 'counter', name: 'creativeWorks', delta: 1 },
    ],
    tags: ['visibility'],
  },
  {
    id: 'act.career.takeBreak',
    group: 'career',
    label: 'Stop working for a while',
    detail: 'No income. A great deal of recovery.',
    requires: [
      { k: 'careerStatus', anyOf: ['employed', 'selfEmployed'] },
      { k: 'num', path: 'runwayMonths', op: '>=', value: 5 },
    ],
    effects: [
      { k: 'career', op: 'setStatus', status: 'break' },
      { k: 'burnout', op: 'load', delta: -26 },
      { k: 'stat', stat: 'stress', delta: -20 },
      { k: 'stat', stat: 'energy', delta: 18 },
      { k: 'habit', habit: 'workaholism', delta: -14 },
    ],
    tags: ['rest'],
  },
  {
    id: 'act.career.returnToWork',
    group: 'career',
    label: 'Start working again',
    detail: 'Re-enter the market at roughly where you left it.',
    requires: [{ k: 'careerStatus', anyOf: ['break', 'unemployed'] }],
    effects: [
      { k: 'career', op: 'setStatus', status: 'employed' },
      { k: 'stat', stat: 'purpose', delta: 6 },
      { k: 'career', op: 'adjust', field: 'performance', delta: -3 },
    ],
    tags: ['pragmatic'],
  },

  /* ---------------- finance ---------------- */
  {
    id: 'act.finance.lifestyleUp',
    group: 'finance',
    label: 'Raise your standard of living',
    detail: 'Nicer everything. Permanently more expensive.',
    requires: [{ k: 'num', path: 'cashFlow', op: '>', value: 4000 }],
    effects: [
      { k: 'lifestyle', delta: 1 },
      { k: 'stat', stat: 'happiness', delta: 6 },
      { k: 'habit', habit: 'spending', delta: 7 },
    ],
    tags: ['lifestyle'],
  },
  {
    id: 'act.finance.lifestyleDown',
    group: 'finance',
    label: 'Cut your spending',
    detail: 'Live below your means on purpose.',
    requires: [{ k: 'num', path: 'lifestyle', op: '>=', value: 1 }],
    effects: [
      { k: 'lifestyle', delta: -1 },
      { k: 'stat', stat: 'happiness', delta: -3 },
      { k: 'habit', habit: 'saving', delta: 9 },
      { k: 'habit', habit: 'spending', delta: -8 },
    ],
    tags: ['discipline'],
  },
  {
    id: 'act.finance.buildBuffer',
    group: 'finance',
    label: 'Prioritise the emergency fund',
    detail: 'Target a year of expenses in reach.',
    effects: [
      { k: 'habit', habit: 'saving', delta: 12 },
      { k: 'stat', stat: 'stress', delta: -5 },
      { k: 'aspiration', op: 'adopt', defId: 'goal.emergencyFund' },
    ],
    tags: ['security'],
  },

  /* ---------------- relationships & family ---------------- */
  {
    id: 'act.rel.investPartner',
    group: 'relationships',
    label: 'Put the year into your partner',
    detail: 'Time, attention, and saying the difficult things.',
    requires: [{ k: 'marital', anyOf: ['dating', 'partnered', 'married'] }],
    effects: [
      { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: 15 },
      { k: 'relationship', who: { r: 'partner' }, dim: 'trust', delta: 10 },
      { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: -12 },
      { k: 'family', op: 'adjustHarmony', delta: 11 },
      { k: 'habit', habit: 'relationshipCare', delta: 10 },
      { k: 'career', op: 'adjust', field: 'performance', delta: -3 },
    ],
    tags: ['relationship'],
  },
  {
    id: 'act.rel.reconnect',
    group: 'relationships',
    label: 'Reconnect with someone you have neglected',
    detail: 'The friendship you keep meaning to maintain.',
    effects: [
      { k: 'relationship', who: { r: 'kind', kind: 'friend', pick: 'weakest' }, dim: 'closeness', delta: 16 },
      { k: 'relationship', who: { r: 'kind', kind: 'friend', pick: 'weakest' }, dim: 'trust', delta: 8 },
      { k: 'habit', habit: 'relationshipCare', delta: 8 },
      { k: 'stat', stat: 'happiness', delta: 6 },
    ],
    tags: ['social'],
  },
  {
    id: 'act.rel.makeFriends',
    group: 'relationships',
    label: 'Deliberately meet new people',
    detail: 'Join things. Say yes. Be slightly uncomfortable.',
    effects: [
      { k: 'person', op: 'add', kind: 'friend', tags: ['deliberate'] },
      { k: 'habit', habit: 'socialising', delta: 10 },
      { k: 'skill', skill: 'social', delta: 4 },
      { k: 'stat', stat: 'energy', delta: -4 },
      { k: 'aspiration', op: 'progress', defId: 'goal.closeFriends', delta: 25 },
    ],
    tags: ['social'],
  },
  {
    id: 'act.family.timeWithChildren',
    group: 'family',
    label: 'Be present with your children',
    detail: 'Fewer evenings at work. More at the table.',
    requires: [{ k: 'hasPerson', kind: 'child' }],
    effects: [
      { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 15 },
      { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: 9 },
      { k: 'family', op: 'adjustHarmony', delta: 9 },
      { k: 'stat', stat: 'purpose', delta: 7 },
      { k: 'career', op: 'adjust', field: 'performance', delta: -5 },
    ],
    tags: ['family'],
  },
  {
    id: 'act.family.callParents',
    group: 'family',
    label: 'Show up for your parents',
    detail: 'Visits, calls, the practical help they will not ask for.',
    requires: [{ k: 'hasPerson', kind: 'parent' }],
    effects: [
      { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: 14 },
      { k: 'relationshipAll', kind: 'parent', dim: 'trust', delta: 8 },
      { k: 'stat', stat: 'purpose', delta: 5 },
      { k: 'stat', stat: 'energy', delta: -3 },
    ],
    tags: ['family'],
  },

  /* ---------------- health & leisure ---------------- */
  {
    id: 'act.health.train',
    group: 'health',
    label: 'Train seriously',
    detail: 'Consistent exercise for a full year.',
    effects: [
      { k: 'habit', habit: 'exercise', delta: 18 },
      { k: 'stat', stat: 'health', delta: 6 },
      { k: 'stat', stat: 'energy', delta: 7 },
      { k: 'stat', stat: 'stress', delta: -7 },
      { k: 'aspiration', op: 'progress', defId: 'goal.fitness', delta: 30 },
    ],
    tags: ['health'],
  },
  {
    id: 'act.health.fixSleep',
    group: 'health',
    label: 'Fix your sleep',
    detail: 'Unglamorous. Enormously effective.',
    effects: [
      { k: 'habit', habit: 'sleep', delta: 18 },
      { k: 'stat', stat: 'energy', delta: 10 },
      { k: 'stat', stat: 'health', delta: 4 },
      { k: 'stat', stat: 'stress', delta: -6 },
    ],
    tags: ['health'],
  },
  {
    id: 'act.health.checkUp',
    group: 'health',
    label: 'Get properly checked over',
    detail: 'Catch things while they are still small.',
    requires: [{ k: 'age', min: 30 }],
    effects: [
      { k: 'cashIncomeMonths', months: -0.3, note: 'health screening' },
      { k: 'stat', stat: 'stress', delta: -4 },
      { k: 'flag', flag: 'screened', value: true },
      { k: 'health', op: 'resolveCondition', id: 'cond.treated' },
    ],
    tags: ['health'],
  },
  {
    id: 'act.leisure.rest',
    group: 'leisure',
    label: 'Do considerably less',
    detail: 'A deliberately quiet year.',
    effects: [
      { k: 'stat', stat: 'energy', delta: 15 },
      { k: 'stat', stat: 'stress', delta: -15 },
      { k: 'burnout', op: 'load', delta: -15 },
      { k: 'career', op: 'adjust', field: 'performance', delta: -5 },
      { k: 'stat', stat: 'happiness', delta: 6 },
    ],
    tags: ['rest'],
  },
  {
    id: 'act.leisure.travel',
    group: 'leisure',
    label: 'Travel properly',
    detail: 'Somewhere far, for long enough to matter.',
    requires: [{ k: 'num', path: 'cash', op: '>=', value: 4000 }],
    effects: [
      { k: 'cashIncomeMonths', months: -1.1, note: 'travel' },
      { k: 'counter', name: 'travelTrips', delta: 1 },
      { k: 'stat', stat: 'happiness', delta: 13 },
      { k: 'stat', stat: 'energy', delta: 8 },
      { k: 'stat', stat: 'stress', delta: -12 },
      { k: 'trait', trait: 'adaptability', delta: 3 },
      { k: 'aspiration', op: 'progress', defId: 'dream.travel', delta: 18 },
    ],
    tags: ['leisure', 'exploration'],
  },
  {
    id: 'act.leisure.makeSomething',
    group: 'leisure',
    label: 'Work on something of your own',
    detail: 'No commercial purpose. Real satisfaction.',
    effects: [
      { k: 'counter', name: 'creativeWorks', delta: 1 },
      { k: 'skill', skill: 'creative', delta: 7 },
      { k: 'stat', stat: 'purpose', delta: 9 },
      { k: 'stat', stat: 'happiness', delta: 6 },
      { k: 'aspiration', op: 'progress', defId: 'dream.craft', delta: 26 },
    ],
    tags: ['creative'],
  },
  {
    id: 'act.leisure.volunteer',
    group: 'leisure',
    label: 'Give your time away',
    detail: 'Useful to people who are not you.',
    effects: [
      { k: 'counter', name: 'contributions', delta: 1 },
      { k: 'stat', stat: 'purpose', delta: 10 },
      { k: 'stat', stat: 'happiness', delta: 5 },
      { k: 'stat', stat: 'energy', delta: -4 },
      { k: 'aspiration', op: 'progress', defId: 'dream.contribute', delta: 20 },
    ],
    tags: ['contribution'],
  },

  /* ---------------- retirement ---------------- */
  {
    id: 'act.retire.now',
    group: 'retire',
    label: 'Retire',
    detail: 'Stop working. For good.',
    requires: [
      { k: 'age', min: 52 },
      { k: 'careerStatus', anyOf: ['employed', 'selfEmployed', 'founder', 'break'] },
    ],
    effects: [
      { k: 'career', op: 'retire' },
      { k: 'stat', stat: 'stress', delta: -14 },
      { k: 'stat', stat: 'purpose', delta: -10 },
      { k: 'stat', stat: 'happiness', delta: 8 },
      { k: 'memory', title: 'Retired', tone: 'milestone', category: 'life', weight: 10 },
      { k: 'triggerEvent', eventId: 'retirement.newStructure', afterYears: 1 },
    ],
    tags: ['retirement'],
  },
  {
    id: 'act.retire.unretire',
    group: 'retire',
    label: 'Go back to work',
    detail: 'Retirement did not suit you.',
    requires: [{ k: 'careerStatus', anyOf: ['retired'] }, { k: 'age', max: 74 }],
    effects: [
      { k: 'career', op: 'setStatus', status: 'employed' },
      { k: 'career', op: 'demote', levels: 1 },
      { k: 'stat', stat: 'purpose', delta: 12 },
      { k: 'flag', flag: 'retired', value: false },
    ],
    tags: ['work'],
  },
];

/* ------------------------------------------------------------------ *
 * Generated actions: investing, education, business, careers
 * ------------------------------------------------------------------ */

function investActions(): PlayerAction[] {
  const tiers: { id: string; label: string; fraction: number }[] = [
    { id: 'half', label: 'half your spare cash', fraction: 0.5 },
    { id: 'all', label: 'all your spare cash', fraction: 1 },
  ];
  const assets: { id: 'conservative' | 'balanced' | 'growth' | 'speculative'; label: string; detail: string }[] = [
    { id: 'conservative', label: 'Conservative assets', detail: 'Low return, low drama, always sellable.' },
    { id: 'balanced', label: 'A balanced portfolio', detail: 'Diversified and unexciting.' },
    { id: 'growth', label: 'Growth assets', detail: 'Higher expected return, real drawdowns.' },
    { id: 'speculative', label: 'Something speculative', detail: 'Might be transformative. Probably not.' },
  ];
  const out: PlayerAction[] = [];
  for (const asset of assets) {
    for (const tier of tiers) {
      out.push({
        id: `act.invest.${asset.id}.${tier.id}`,
        group: 'invest',
        label: `Invest ${tier.label} in ${asset.label.toLowerCase()}`,
        detail: asset.detail,
        requires: [{ k: 'num', path: 'cash', op: '>=', value: 2500 }],
        effects: [],
        dynamic: (state) => {
          const amount = surplus(state, tier.fraction);
          if (amount < 500) return [];
          return [
            { k: 'invest', op: 'buy', assetId: asset.id, amount },
            { k: 'habit', habit: 'investing', delta: 6 },
          ];
        },
        tags: ['finance'],
      });
    }
  }
  out.push({
    id: 'act.invest.sellDown',
    group: 'invest',
    label: 'Sell investments and hold cash',
    detail: 'Certainty now, at the cost of compounding.',
    requires: [{ k: 'num', path: 'investments', op: '>=', value: 5000 }],
    effects: [],
    dynamic: (state) => {
      const amount = Math.round(investableValue(state) * 0.5);
      return [
        { k: 'invest', op: 'sell', assetId: 'growth', amount },
        { k: 'invest', op: 'sell', assetId: 'speculative', amount },
        { k: 'stat', stat: 'stress', delta: -5 },
        { k: 'habit', habit: 'investing', delta: -8 },
      ];
    },
    tags: ['security'],
  });
  out.push({
    id: 'act.invest.payDebt',
    group: 'invest',
    label: 'Throw everything at your debt',
    detail: 'Guaranteed return equal to the interest rate.',
    requires: [{ k: 'num', path: 'debt', op: '>=', value: 3000 }],
    effects: [],
    dynamic: (state) => {
      const amount = Math.max(0, Math.floor(state.finance.cash * 0.8));
      if (amount < 300) return [];
      return [
        { k: 'debt', op: 'pay', amount },
        { k: 'stat', stat: 'stress', delta: -7 },
        { k: 'habit', habit: 'saving', delta: 6 },
      ];
    },
    tags: ['discipline'],
  });
  return out;
}

function educationActions(): PlayerAction[] {
  return EDUCATION_PROGRAM_LIST.map((program) => ({
    id: `act.education.${program.id}`,
    group: 'education' as const,
    label: `Enrol: ${program.label}`,
    detail: `${program.years} year${program.years > 1 ? 's' : ''} · ${program.annualCost > 0 ? `$${program.annualCost.toLocaleString('en-US')}/yr` : 'free'} · ${program.blurb}`,
    requires: [
      { k: 'careerStatus', anyOf: ['none', 'employed', 'selfEmployed', 'unemployed', 'break', 'founder'] },
      ...(program.minAge !== undefined ? [{ k: 'age' as const, min: program.minAge }] : []),
      ...(program.maxAge !== undefined ? [{ k: 'age' as const, max: program.maxAge }] : []),
      ...(program.requires ?? []),
    ],
    effects: [
      { k: 'education', op: 'enroll', programId: program.id },
      { k: 'habit', habit: 'learning', delta: 8 },
      { k: 'stat', stat: 'purpose', delta: 5 },
    ],
    tags: ['education'],
  }));
}

function careerActions(): PlayerAction[] {
  return CAREER_FIELD_LIST.map((field) => {
    const requires: Condition[] = [
      { k: 'careerStatus', anyOf: ['none', 'unemployed', 'break', 'employed'] },
    ];
    if (field.entry.education) requires.push({ k: 'educationAtLeast', level: field.entry.education });
    if (field.entry.credential) requires.push({ k: 'hasCredential', id: field.entry.credential });
    for (const [skill, value] of Object.entries(field.entry.skills ?? {})) {
      requires.push({ k: 'skill', skill: skill as never, op: '>=', value: value ?? 0 });
    }
    return {
      id: `act.career.enter.${field.id}`,
      group: 'career' as const,
      label: `Work in ${field.label}`,
      detail: field.blurb,
      requires,
      effects: [
        { k: 'career', op: 'setField', fieldId: field.id },
        { k: 'stat', stat: 'purpose', delta: 5 },
      ],
      tags: ['career', ...field.tags],
    };
  });
}

function businessActions(): PlayerAction[] {
  const out: PlayerAction[] = BUSINESS_ARCHETYPE_LIST.map((arch) => ({
    id: `act.business.start.${arch.id}`,
    group: 'business' as const,
    label: `Start: ${arch.label}`,
    detail: `Needs about $${arch.startingCapital.toLocaleString('en-US')} · ${arch.blurb}`,
    requires: [
      { k: 'flag', flag: 'founder', value: false },
      { k: 'num', path: 'cash', op: '>=', value: arch.startingCapital },
      ...(arch.requires ?? []),
    ],
    effects: [
      { k: 'business', op: 'start', archetypeId: arch.id, capital: arch.startingCapital },
      { k: 'stat', stat: 'purpose', delta: 12 },
      { k: 'stat', stat: 'stress', delta: 10 },
      { k: 'aspiration', op: 'progress', defId: 'dream.business', delta: 100 },
      { k: 'memory', title: `Started ${arch.label.toLowerCase()}`, tone: 'milestone', category: 'business', weight: 8 },
    ],
    tags: ['entrepreneurship', ...arch.tags],
  }));

  out.push(
    {
      id: 'act.business.reinvest',
      group: 'business',
      label: 'Reinvest in the business',
      detail: 'Capital in, capacity out.',
      requires: [{ k: 'num', path: 'businessRevenue', op: '>', value: 0 }, { k: 'num', path: 'cash', op: '>=', value: 5000 }],
      effects: [],
      dynamic: (state) => [
        { k: 'business', op: 'invest', amount: Math.floor(Math.max(0, state.finance.cash) * 0.5) },
        { k: 'stat', stat: 'energy', delta: -4 },
      ],
      tags: ['entrepreneurship'],
    },
    {
      id: 'act.business.sell',
      group: 'business',
      label: 'Sell the business',
      detail: 'Take the valuation and walk away.',
      requires: [{ k: 'num', path: 'businessValuation', op: '>=', value: 10000 }],
      effects: [
        { k: 'business', op: 'exit', multiple: 0.9 },
        { k: 'stat', stat: 'purpose', delta: -10 },
        { k: 'stat', stat: 'stress', delta: -12 },
      ],
      tags: ['exit'],
    },
    {
      id: 'act.business.close',
      group: 'business',
      label: 'Close the business',
      detail: 'Stop the bleeding while there is something left.',
      requires: [{ k: 'num', path: 'businessHealth', op: '<=', value: 40 }, { k: 'num', path: 'businessRevenue', op: '>', value: 0 }],
      effects: [
        { k: 'business', op: 'fail' },
        { k: 'stat', stat: 'happiness', delta: -10 },
        { k: 'stat', stat: 'stress', delta: -8 },
        { k: 'trait', trait: 'resilience', delta: 5 },
      ],
      tags: ['exit'],
    },
  );
  return out;
}

export const ALL_ACTIONS: readonly PlayerAction[] = [
  ...STATIC_ACTIONS,
  ...investActions(),
  ...educationActions(),
  ...careerActions(),
  ...businessActions(),
];

export const ACTIONS_BY_ID: Record<string, PlayerAction> = Object.fromEntries(
  ALL_ACTIONS.map((a) => [a.id, a]),
);

export const ACTIONS_BY_GROUP: Record<string, PlayerAction[]> = (() => {
  const map: Record<string, PlayerAction[]> = {};
  for (const action of ALL_ACTIONS) (map[action.group] ??= []).push(action);
  return map;
})();

/** Effects for an action, including any dynamically computed amounts. */
export function actionEffects(state: PlayerState, action: PlayerAction): Effect[] {
  const dynamic = action.dynamic ? action.dynamic(state) : [];
  return [...action.effects, ...dynamic];
}

/**
 * Actions are only offered while the player can bear them. A player with two
 * months of runway should not be shown "travel properly" as a live option.
 */
export function actionAffordable(state: PlayerState, action: PlayerAction): boolean {
  if (action.group === 'leisure' && action.id === 'act.leisure.travel') {
    return runwayMonths(state) >= 2;
  }
  if (action.group === 'invest') {
    return clamp(surplus(state, 1), 0, Number.MAX_SAFE_INTEGER) >= 500 || action.id === 'act.invest.payDebt' || action.id === 'act.invest.sellDown';
  }
  return true;
}
